import React, { useState } from "react";
import { cn } from "../lib/utils";
import { 
  CheckCircle2, 
  Clock, 
  AlertTriangle, 
  Play,
  Flag,
  Info,
  DollarSign
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Slider } from "./ui/slider";

/**
 * Unified Project Flow - Compact timeline with stages on one screen
 */

// Role colors
const ROLE_COLORS = {
  team: { bg: "bg-blue-500/20", border: "border-blue-500/40", text: "text-blue-400", solid: "bg-blue-500", label: "Team" },
  partner: { bg: "bg-purple-500/20", border: "border-purple-500/40", text: "text-purple-400", solid: "bg-purple-500", label: "Partner" },
  client: { bg: "bg-orange-500/20", border: "border-orange-500/40", text: "text-orange-400", solid: "bg-orange-500", label: "Client" }
};

// Format date as "Mar 15"
const formatDate = (date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

// Add WORKING days (skip weekends)
const addWorkingDays = (date, days) => {
  const result = new Date(date);
  let added = 0;
  while (added < days) {
    result.setDate(result.getDate() + 1);
    const dayOfWeek = result.getDay();
    // Skip Saturday (6) and Sunday (0)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      added++;
    }
  }
  return result;
};

// Count working days between dates
const countWorkingDays = (start, end) => {
  let count = 0;
  const current = new Date(start);
  while (current < end) {
    current.setDate(current.getDate() + 1);
    const dayOfWeek = current.getDay();
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      count++;
    }
  }
  return count;
};

/**
 * Compact stage card
 */
const CompactStageCard = ({ stage, index, isActive, isCompleted, expectedDate, onClick }) => {
  const roleStyle = ROLE_COLORS[stage.owner_role] || ROLE_COLORS.team;
  const isOverdue = stage.status === 'overdue' || (stage.actualDays && stage.actualDays > stage.duration_days);
  
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div 
          className="flex flex-col items-center cursor-pointer group"
          onClick={() => onClick?.(stage)}
        >
          {/* Expected date */}
          <div className="text-[10px] text-zinc-500 mb-1 h-4">
            {expectedDate && formatDate(expectedDate)}
          </div>
          
          {/* Stage card */}
          <div className={cn(
            "relative w-20 h-16 rounded-lg border-2 transition-all",
            "flex flex-col items-center justify-center p-1",
            "group-hover:scale-105",
            isCompleted 
              ? "bg-zinc-800/30 border-green-500/50" 
              : isActive 
                ? isOverdue
                  ? "bg-red-500/20 border-red-500 ring-2 ring-red-500/30"
                  : `${roleStyle.bg} ${roleStyle.border} ring-2 ring-orange-500/30`
                : "bg-zinc-800/50 border-zinc-700"
          )}>
            {/* Status icon */}
            <div className="absolute -top-2 -right-2">
              {isCompleted ? (
                <CheckCircle2 className="w-4 h-4 text-green-400 bg-zinc-900 rounded-full" />
              ) : isActive ? (
                isOverdue ? (
                  <AlertTriangle className="w-4 h-4 text-red-400 bg-zinc-900 rounded-full" />
                ) : (
                  <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse" />
                )
              ) : null}
            </div>
            
            {/* Role dot */}
            <div className={cn("w-2 h-2 rounded-full mb-1", roleStyle.solid)} />
            
            {/* Stage name */}
            <div className={cn(
              "text-[9px] font-medium text-center leading-tight",
              isCompleted ? "text-zinc-500" : "text-zinc-200"
            )}>
              {stage.name}
            </div>
            
            {/* Duration */}
            {stage.duration_days && (
              <div className="text-[8px] text-zinc-500 mt-0.5">
                {stage.duration_days}d
              </div>
            )}
          </div>
          
          {/* You are here */}
          {isActive && !isCompleted && (
            <div className="mt-2 flex flex-col items-center">
              <div className="w-px h-2 bg-orange-500/50" />
              <div className="text-[8px] text-orange-400 whitespace-nowrap">You are here</div>
            </div>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent className="bg-zinc-800 border-zinc-700">
        <div className="text-sm">
          <div className="font-medium">{stage.name}</div>
          <div className="text-zinc-400">{roleStyle.label} • {stage.duration_days || '-'} days</div>
          {stage.description && <div className="text-zinc-500 text-xs mt-1">{stage.description}</div>}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

/**
 * Connector arrow
 */
const Connector = ({ isCompleted }) => (
  <div className={cn(
    "w-3 h-px mt-8",
    isCompleted ? "bg-green-500/50" : "bg-zinc-700"
  )} />
);

/**
 * Dot Flow View - Version 2 with dots representing days
 * Client dots change dynamically based on slider
 */
const DotFlowView = ({ stages, currentStageIndex, clientMaxDays, delayFeePerDay, startDate, onStageClick, clientDays, setClientDays }) => {
  // Calculate team+partner days (fixed)
  const fixedDays = stages.filter(s => s.owner_role !== 'client').reduce((sum, s) => sum + (s.duration_days || 0), 0);
  const totalWithClient = fixedDays + clientDays;
  const deliveryDate = addWorkingDays(startDate, totalWithClient);
  
  // Calculate how many client dots to show per stage
  const getClientDotsForStage = (stage) => {
    if (stage.owner_role !== 'client') return stage.duration_days || 0;
    
    // Calculate proportional dots based on slider
    // If slider < max, reduce client dots proportionally
    const stageDays = stage.duration_days || 0;
    const proportion = clientDays / clientMaxDays;
    return Math.round(stageDays * proportion);
  };
  
  // Calculate overdue days
  const overdueDays = Math.max(0, clientDays - clientMaxDays);
  
  // Render dots for a stage
  const renderDots = (stage, index) => {
    const roleStyle = ROLE_COLORS[stage.owner_role] || ROLE_COLORS.team;
    const isCompleted = index < currentStageIndex || stage.status === 'completed';
    const isActive = index === currentStageIndex || stage.status === 'active';
    const isClient = stage.owner_role === 'client';
    
    // For client stages, use dynamic count based on slider
    const dotsToShow = isClient ? getClientDotsForStage(stage) : (stage.duration_days || 0);
    const maxDots = stage.duration_days || 0;
    
    if (maxDots === 0) return null;
    
    return (
      <Tooltip key={stage.id || index}>
        <TooltipTrigger asChild>
          <div 
            className={cn(
              "flex items-center gap-0.5 px-2 py-2 rounded cursor-pointer transition-all",
              "hover:bg-zinc-700/50 min-w-[24px] min-h-[24px]",
              isActive && "ring-1 ring-orange-500/50 bg-zinc-800/50",
              isCompleted && "opacity-50"
            )}
            onClick={() => onStageClick?.(stage)}
          >
            {/* Show filled dots */}
            {Array.from({ length: dotsToShow }).map((_, i) => (
              <div
                key={`filled-${i}`}
                className={cn(
                  "w-3 h-3 rounded-full transition-all",
                  roleStyle.solid,
                  isCompleted && "opacity-60"
                )}
              />
            ))}
            {/* Show empty dots for remaining (client stages only) */}
            {isClient && Array.from({ length: maxDots - dotsToShow }).map((_, i) => (
              <div
                key={`empty-${i}`}
                className="w-3 h-3 rounded-full border border-orange-500/30 bg-transparent"
              />
            ))}
          </div>
        </TooltipTrigger>
        <TooltipContent className="bg-zinc-800 border-zinc-700">
          <div className="text-sm">
            <div className="font-medium">{stage.name}</div>
            <div className="text-zinc-400">{roleStyle.label} • {maxDots} day{maxDots > 1 ? 's' : ''}</div>
            {isClient && dotsToShow !== maxDots && (
              <div className="text-orange-400 text-xs mt-1">
                Planning to use: {dotsToShow} of {maxDots} days
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  };

  return (
    <div className="mt-6 pt-6 border-t border-zinc-800">
      <div className="text-sm text-zinc-400 font-medium mb-4">Visual Timeline (by days)</div>
      
      {/* Dot flow */}
      <div className="flex items-center gap-0.5 flex-wrap mb-4 p-3 bg-zinc-800/30 rounded-lg">
        {stages.map((stage, index) => (
          <React.Fragment key={stage.id || index}>
            {renderDots(stage, index)}
            {index < stages.length - 1 && (stage.duration_days || 0) > 0 && (
              <div className="w-2 h-px bg-zinc-600" />
            )}
          </React.Fragment>
        ))}
        {/* Show overdue dots */}
        {overdueDays > 0 && (
          <>
            <div className="w-2 h-px bg-red-500" />
            <div className="flex items-center gap-0.5 px-2 py-2 bg-red-500/10 rounded border border-red-500/30">
              {Array.from({ length: overdueDays }).map((_, i) => (
                <div key={`overdue-${i}`} className="w-3 h-3 rounded-full bg-red-500" />
              ))}
            </div>
          </>
        )}
      </div>
      
      {/* Legend with slider */}
      <div className="flex items-center justify-between gap-6">
        {/* Legend */}
        <div className="flex items-center gap-4">
          {Object.entries(ROLE_COLORS).map(([role, style]) => (
            <div key={role} className="flex items-center gap-1.5">
              <div className={cn("w-3 h-3 rounded-full", style.solid)} />
              <span className="text-xs text-zinc-400">{style.label}</span>
            </div>
          ))}
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full border border-orange-500/30" />
            <span className="text-xs text-zinc-500">Saved time</span>
          </div>
        </div>
        
        {/* Client slider */}
        <div className="flex items-center gap-3 flex-1 max-w-sm">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-orange-500" />
            <span className="text-xs text-zinc-400 whitespace-nowrap">Client time:</span>
          </div>
          <Slider
            value={[clientDays]}
            onValueChange={([val]) => setClientDays(val)}
            min={0}
            max={clientMaxDays + 4} // Allow some overdue
            step={1}
            className="flex-1"
          />
          <span className={cn(
            "text-xs font-medium w-20 text-right",
            clientDays === 0 ? "text-green-400" : 
            clientDays > clientMaxDays ? "text-red-400" : 
            clientDays < clientMaxDays ? "text-green-400" : "text-orange-400"
          )}>
            {clientDays}/{clientMaxDays}d
            {clientDays > clientMaxDays && ` +${clientDays - clientMaxDays}`}
          </span>
        </div>
      </div>
      
      {/* Result */}
      <div className="mt-3 flex items-center justify-between text-sm">
        <div className="flex items-center gap-4">
          <div>
            <span className="text-zinc-500">Fixed (Team+Partner): </span>
            <span className="text-zinc-300 font-medium">{fixedDays}d</span>
          </div>
          <div>
            <span className="text-zinc-500">Client: </span>
            <span className={cn(
              "font-medium",
              clientDays === 0 ? "text-green-400" : 
              clientDays > clientMaxDays ? "text-red-400" : 
              clientDays < clientMaxDays ? "text-green-400" : "text-orange-400"
            )}>
              {clientDays}d
              {clientDays < clientMaxDays && ` (${clientMaxDays - clientDays}d saved!)`}
              {clientDays > clientMaxDays && ` (+${clientDays - clientMaxDays}d overdue)`}
            </span>
          </div>
        </div>
        <div>
          <span className="text-zinc-500">Delivery: </span>
          <span className={cn(
            "font-medium",
            clientDays > clientMaxDays ? "text-red-400" : "text-zinc-50"
          )}>{formatDate(deliveryDate)}</span>
          <span className="text-zinc-500"> ({totalWithClient}d)</span>
        </div>
      </div>
    </div>
  );
};

/**
 * Main component
 */
const ProjectFlowUnified = ({
  projectName = "360° Tour",
  startDate = new Date(),
  stages = [],
  currentStageIndex = 0,
  contractAmount = 50000,
  delayRatePercent = 0.1,
  className = ""
}) => {
  const [selectedStage, setSelectedStage] = useState(null);
  
  // Calculate totals
  const teamDays = stages.filter(s => s.owner_role !== 'client').reduce((sum, s) => sum + (s.duration_days || 0), 0);
  const clientMaxDays = stages.filter(s => s.owner_role === 'client').reduce((sum, s) => sum + (s.duration_days || 0), 0);
  const totalDays = stages.reduce((sum, s) => sum + (s.duration_days || 0), 0);
  
  // Client days slider state (starts at max)
  const [clientDays, setClientDays] = useState(clientMaxDays);
  
  // Calculate delay based on slider
  const accumulatedDelay = Math.max(0, clientDays - clientMaxDays);
  
  // Dates - NOW DEPENDENT ON clientDays slider
  const now = new Date();
  const daysElapsed = countWorkingDays(startDate, now);
  const fastestDays = teamDays; // Only team+partner work
  const maxDays = totalDays; // Team + Partner + Client max
  const currentDays = teamDays + clientDays; // Dynamic based on slider
  
  const maxDate = addWorkingDays(startDate, maxDays);
  const fastestDate = addWorkingDays(startDate, fastestDays);
  const currentDate = addWorkingDays(startDate, currentDays);
  
  // Fee calculation
  const delayFeePerDay = (contractAmount * delayRatePercent) / 100;
  const currentFee = accumulatedDelay * delayFeePerDay;
  
  // Timeline progress
  const fullProgress = maxDays > 0 ? (daysElapsed / maxDays) * 100 : 0;
  const deliveryProgress = maxDays > fastestDays ? ((currentDays - fastestDays) / (maxDays - fastestDays)) * 100 : 0;
  
  // Calculate expected date for each stage
  const getExpectedDate = (index) => {
    let days = 0;
    for (let i = 0; i <= index; i++) {
      days += stages[i]?.duration_days || 0;
    }
    return addWorkingDays(startDate, days);
  };

  return (
    <TooltipProvider>
      <div className={cn("bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden", className)}>
        {/* Header */}
        <div className="px-6 py-3 border-b border-zinc-800 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-zinc-50">{projectName}</h2>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-2 text-sm cursor-help">
                <DollarSign className="w-4 h-4 text-zinc-500" />
                <span className="text-zinc-400">Delay fee:</span>
                <span className="text-zinc-50 font-medium">${delayFeePerDay.toFixed(0)}/day</span>
                <Info className="w-3.5 h-3.5 text-zinc-500" />
              </div>
            </TooltipTrigger>
            <TooltipContent className="bg-zinc-800 border-zinc-700">
              <div className="text-sm">
                <div>Contract: ${contractAmount.toLocaleString()}</div>
                <div>Rate: {delayRatePercent}%/day = ${delayFeePerDay.toFixed(0)}/day</div>
              </div>
            </TooltipContent>
          </Tooltip>
        </div>

        <div className="p-4 space-y-4">
          {/* Timeline bars - minimalist */}
          <div className="grid grid-cols-2 gap-6">
            {/* Project Timeline */}
            <div className="space-y-1">
              <div className="flex justify-between items-center text-xs">
                <div className="flex items-center gap-1">
                  <Play className="w-3 h-3 text-green-400 fill-green-400" />
                  <span className="text-zinc-500">Start</span>
                  <span className="text-zinc-300">{formatDate(startDate)}</span>
                </div>
                <span className={cn(
                  accumulatedDelay > 0 ? "text-red-400" : "text-orange-400"
                )}>
                  Day {daysElapsed} <span className="text-zinc-500">of {currentDays}</span>
                </span>
                <div className="flex items-center gap-1">
                  <Flag className={cn("w-3 h-3", accumulatedDelay > 0 ? "text-red-400" : "text-zinc-500")} />
                  <span className="text-zinc-500">Est.</span>
                  <span className={cn(
                    accumulatedDelay > 0 ? "text-red-400" : "text-zinc-300"
                  )}>{formatDate(currentDate)}</span>
                </div>
              </div>
              <div className="relative h-2 bg-zinc-800 rounded-full overflow-hidden">
                <div 
                  className={cn(
                    "h-full rounded-full transition-all duration-300",
                    accumulatedDelay > 0 
                      ? "bg-gradient-to-r from-green-500 via-orange-500 to-red-500" 
                      : "bg-gradient-to-r from-green-500 via-blue-500 to-orange-500"
                  )}
                  style={{ width: `${Math.min((daysElapsed / currentDays) * 100, 100)}%` }}
                />
                <div 
                  className={cn(
                    "absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border-2 shadow transition-all",
                    accumulatedDelay > 0 ? "border-red-500" : "border-orange-500"
                  )}
                  style={{ left: `calc(${Math.min((daysElapsed / currentDays) * 100, 100)}% - 6px)` }}
                />
              </div>
            </div>

            {/* Delivery Window */}
            <div className="space-y-1">
              <div className="flex justify-between items-center text-xs">
                <div>
                  <span className="text-zinc-500">Fastest </span>
                  <span className="text-green-400 font-medium">{fastestDays}d</span>
                  <span className="text-zinc-600 ml-1">({formatDate(fastestDate)})</span>
                </div>
                <div>
                  <span className="text-zinc-500">Current </span>
                  <span className={cn(
                    "font-medium transition-colors",
                    clientDays === 0 ? "text-green-400" : 
                    accumulatedDelay > 0 ? "text-red-400" : 
                    clientDays < clientMaxDays ? "text-green-400" : "text-orange-400"
                  )}>
                    {currentDays}d
                  </span>
                  {clientDays < clientMaxDays && clientDays > 0 && (
                    <span className="text-green-400 ml-1">(-{clientMaxDays - clientDays})</span>
                  )}
                  {accumulatedDelay > 0 && (
                    <span className="text-red-400 ml-1">(+{accumulatedDelay})</span>
                  )}
                </div>
                <div>
                  <span className="text-zinc-500">Max </span>
                  <span className="text-zinc-400 font-medium">{maxDays}d</span>
                </div>
              </div>
              <div className="relative h-2 bg-zinc-800 rounded-full overflow-visible">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500/30 via-yellow-500/30 to-orange-500/30 rounded-full" />
                {/* Overdue extension */}
                {accumulatedDelay > 0 && (
                  <div 
                    className="absolute top-0 bottom-0 bg-red-500/40 rounded-r-full"
                    style={{ 
                      left: '100%',
                      width: `${(accumulatedDelay / (maxDays - fastestDays)) * 100}%`
                    }}
                  />
                )}
                <div 
                  className={cn(
                    "absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border-2 shadow transition-all",
                    clientDays === 0 ? "border-green-500" :
                    accumulatedDelay > 0 ? "border-red-500" : 
                    clientDays < clientMaxDays ? "border-green-500" : "border-orange-500"
                  )}
                  style={{ 
                    left: `calc(${Math.min(Math.max(deliveryProgress, 0), accumulatedDelay > 0 ? 120 : 100)}% - 6px)` 
                  }}
                />
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-6 py-2">
            {Object.entries(ROLE_COLORS).map(([role, style]) => (
              <div key={role} className="flex items-center gap-1.5">
                <div className={cn("w-2.5 h-2.5 rounded-full", style.solid)} />
                <span className="text-xs text-zinc-400">{style.label}</span>
              </div>
            ))}
            {accumulatedDelay > 0 && (
              <div className="flex items-center gap-1.5 ml-4 pl-4 border-l border-zinc-700">
                <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
                <span className="text-xs text-red-400">+{accumulatedDelay}d delay</span>
                <span className="text-xs text-red-400 font-medium">${currentFee.toFixed(0)} fee</span>
              </div>
            )}
          </div>

          {/* Flow stages - compact, all on screen */}
          <div className="flex items-start justify-center gap-1 py-4 overflow-x-auto">
            {stages.map((stage, index) => {
              const isCompleted = index < currentStageIndex || stage.status === 'completed';
              const isActive = index === currentStageIndex || stage.status === 'active';
              const expectedDate = getExpectedDate(index);
              
              return (
                <React.Fragment key={stage.id || index}>
                  <CompactStageCard
                    stage={stage}
                    index={index}
                    isActive={isActive}
                    isCompleted={isCompleted}
                    expectedDate={expectedDate}
                    onClick={setSelectedStage}
                  />
                  {index < stages.length - 1 && <Connector isCompleted={isCompleted} />}
                </React.Fragment>
              );
            })}
          </div>

          {/* VERSION 2: Dot Flow with slider */}
          <DotFlowView 
            stages={stages}
            currentStageIndex={currentStageIndex}
            clientMaxDays={clientMaxDays}
            delayFeePerDay={delayFeePerDay}
            startDate={startDate}
            onStageClick={setSelectedStage}
            clientDays={clientDays}
            setClientDays={setClientDays}
          />
        </div>

        {/* Stage detail dialog */}
        <Dialog open={!!selectedStage} onOpenChange={() => setSelectedStage(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedStage && (
                  <>
                    <div className={cn("w-3 h-3 rounded-full", ROLE_COLORS[selectedStage.owner_role]?.solid)} />
                    {selectedStage.name}
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            {selectedStage && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">Owner</span>
                    <p className={ROLE_COLORS[selectedStage.owner_role]?.text}>
                      {ROLE_COLORS[selectedStage.owner_role]?.label}
                    </p>
                  </div>
                  <div>
                    <span className="text-zinc-500">Duration</span>
                    <p className="text-zinc-200">{selectedStage.duration_days || '-'} days</p>
                  </div>
                </div>
                {selectedStage.description && (
                  <div>
                    <span className="text-zinc-500 text-sm">Description</span>
                    <p className="text-zinc-300 text-sm mt-1">{selectedStage.description}</p>
                  </div>
                )}
                {selectedStage.owner_role === 'client' && (
                  <div className="p-3 bg-orange-500/10 rounded-lg border border-orange-500/20">
                    <p className="text-sm text-orange-300">
                      <strong>Client action required:</strong> You have {selectedStage.duration_days || 2} days to provide feedback. 
                      Delays will affect the project timeline and may incur fees.
                    </p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

// Demo stages - 360 Tour workflow (14 steps)
export const DEMO_FLOW_STAGES = [
  { id: "s1", name: "Materials", owner_role: "client", duration_days: 2, status: "completed", actualDays: 2 },
  { id: "s2", name: "Modeling", owner_role: "team", duration_days: 4, status: "completed" },
  { id: "s3", name: "Partner Review", owner_role: "partner", duration_days: 1, status: "active" },
  { id: "s4", name: "Revisions 1", owner_role: "team", duration_days: 2, status: "pending" },
  { id: "s5", name: "Partner Review 2", owner_role: "partner", duration_days: 1, status: "pending" },
  { id: "s6", name: "Revisions 2", owner_role: "team", duration_days: 2, status: "pending" },
  { id: "s7", name: "Client Review 1", owner_role: "client", duration_days: 2, status: "pending" },
  { id: "s8", name: "Client Fix 1", owner_role: "team", duration_days: 2, status: "pending" },
  { id: "s9", name: "Partner QA", owner_role: "partner", duration_days: 1, status: "pending" },
  { id: "s10", name: "Client Fix 2", owner_role: "team", duration_days: 2, status: "pending" },
  { id: "s11", name: "Client Review 2", owner_role: "client", duration_days: 2, status: "pending" },
  { id: "s12", name: "Client Fix 3", owner_role: "team", duration_days: 2, status: "pending" },
  { id: "s13", name: "Partner Final", owner_role: "partner", duration_days: 1, status: "pending" },
  { id: "s14", name: "Final Fix", owner_role: "team", duration_days: 1, status: "pending" }
];

export default ProjectFlowUnified;
